import { useCallback, useMemo } from 'react'

import type { ApplicantStage } from 'types/graphql'

import { navigate } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_PIPELINE_SUMMARY } from 'src/graphql/pipelineMetrics.graphql'
import {
  buildCategoryStatRowItems,
  StatRowsCardView,
  type CategoryDefinition,
} from 'src/pages/AnalyticsPage/components/StatRowsCard'
import { useRefetchOnKeyChange } from 'src/pages/AnalyticsPage/components/trendChartHelpers'

import { buildApplicantsFilterUrl } from '../buildApplicantsFilterUrl'

import { STAGE_ORDER } from './stagePalette'

const INFO_TEXT =
  'Counts of applicants whose stage timestamp falls in the selected date range. Each stage uses its own column on Applicant — Hired uses hiredDate, Disqualified uses disqualifiedDate, etc.'

interface PipelineSummaryShape {
  counts: ReadonlyArray<{ stage: ApplicantStage; count: number }>
  total: number
}

export interface PipelineSummaryContainerProps {
  /** Page-level FiltersBar state — the resolver honors every facet. */
  filters: {
    dateRange: { gte: Date; lte: Date }
    recruiters?: string[]
    jobListings?: string[]
    linkTypes?: string[]
    jobTypes?: string[]
  }
  /**
   * Bumped by the page-level Refresh button to trigger a manual refetch.
   * Mount value is 0 — the initial useQuery already fetches, so the first
   * change to this prop (i.e. ≥ 1) is what drives a refetch.
   */
  refreshKey: number
}

const PipelineSummaryContainer = ({
  filters,
  refreshKey,
}: PipelineSummaryContainerProps) => {
  const { dateRange } = filters
  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_PIPELINE_SUMMARY, {
    variables: { input: { filters } },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  useRefetchOnKeyChange(refetch, refreshKey)

  const summary = data?.pipelineSummary as PipelineSummaryShape | undefined

  const categories: ReadonlyArray<CategoryDefinition> = useMemo(
    () =>
      STAGE_ORDER.map((s) => ({
        id: s.stage,
        label: s.label,
        color: s.color,
      })),
    []
  )

  const counts: Readonly<Record<string, number>> = useMemo(() => {
    const out: Record<string, number> = {}
    for (const c of summary?.counts ?? []) out[c.stage] = c.count
    return out
  }, [summary])

  const items = useMemo(
    () =>
      buildCategoryStatRowItems(
        categories,
        counts,
        (stageId) => () =>
          navigate(
            buildApplicantsFilterUrl({
              stage: stageId as ApplicantStage,
              dateRange,
              recruiterIds: filters.recruiters,
              jobListingIds: filters.jobListings,
              leadSources: filters.linkTypes,
            })
          )
      ),
    [categories, counts, dateRange, filters]
  )

  return (
    <StatRowsCardView
      title="Pipeline Summary"
      info={INFO_TEXT}
      items={items}
      loading={loading}
      pctSuffix="of period"
      emptySubtext="No applicants"
    />
  )
}

export default PipelineSummaryContainer
